import React, { useState } from 'react';
import { CheckCircle2, Download, Landmark, User, Handshake } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const SectionHeader = ({ icon: Icon, num, color, title, subtitle }) => (
  <div className={`flex items-start gap-3 rounded-xl px-4 py-3 mb-3 ${color.bg}`}>
    <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${color.badge}`}>
      <Icon className="w-4 h-4 text-white" aria-hidden="true" />
    </div>
    <div>
      <h2 className={`text-base font-bold ${color.text}`}>{num}. {title}</h2>
      <p className="text-xs text-slate-500 italic">{subtitle}</p>
    </div>
  </div>
);

export default function BoardVisionGoals() {
  const [formData, setFormData] = useState({
    board_member_name: '', form_date: new Date().toISOString().slice(0, 10),
    term: '', committees: '', goal_organization: '', goal_role: '', support_needed: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [submittedId, setSubmittedId] = useState(null);

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/api/board/vision-goals`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setSubmittedId(data.id);
        window.scrollTo({ top: 0 });
      } else {
        toast.error(data.detail || 'Submission failed. Please try again.');
      }
    } catch {
      toast.error('Submission failed. Please check your connection and try again.');
    }
    setSubmitting(false);
  };

  if (submittedId) {
    return (
      <div className="min-h-screen bg-[#faf8f3]">
        <Header />
        <main id="main-content" tabIndex={-1} className="pt-36 pb-24">
          <div className="max-w-xl mx-auto px-4 text-center bg-white rounded-2xl border border-slate-200 shadow-sm p-10" data-testid="vision-goals-success">
            <CheckCircle2 className="w-14 h-14 text-teal-600 mx-auto mb-4" aria-hidden="true" />
            <h1 className="text-3xl font-bold text-slate-900 mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>
              Thank You!
            </h1>
            <p className="text-slate-600 mb-6">
              Your Vision &amp; Goals form has been submitted to DCELC. You can download a completed copy for your records below.
            </p>
            <a href={`${API}/api/board/vision-goals/${submittedId}/pdf`} download>
              <Button className="bg-teal-700 hover:bg-teal-800 text-white" data-testid="vision-goals-download-pdf">
                <Download className="w-4 h-4 mr-2" aria-hidden="true" />
                Download My Completed Form (PDF)
              </Button>
            </a>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#faf8f3]">
      <Header />
      <main id="main-content" tabIndex={-1} className="pt-32 pb-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h1 className="text-4xl sm:text-5xl font-bold text-[#0a1628] mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
              DCELC Board Vision &amp; Goals Form
            </h1>
            <p className="text-teal-700 font-semibold">Our Vision. Our Commitment. Our Impact.</p>
            <p className="text-sm text-slate-600 mt-4 max-w-2xl mx-auto">
              As a member of the DC Early Learning Collaborative (DCELC) Board of Directors, your insight, experience,
              and leadership are essential to advancing our mission and strengthening early learning in the District of
              Columbia. Please share your goal and vision for DCELC as an organization and for your individual role as
              a board member.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 sm:p-10 space-y-6" data-testid="vision-goals-form">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <label htmlFor="board_member_name" className="block text-sm font-bold text-teal-800 uppercase tracking-wide mb-1">
                  Board Member Name <span className="text-amber-600" aria-hidden="true">*</span>
                </label>
                <Input id="board_member_name" name="board_member_name" required value={formData.board_member_name} onChange={handleChange} data-testid="vg-name" />
              </div>
              <div>
                <label htmlFor="form_date" className="block text-sm font-bold text-teal-800 uppercase tracking-wide mb-1">
                  Date <span className="text-amber-600" aria-hidden="true">*</span>
                </label>
                <Input id="form_date" name="form_date" type="date" required value={formData.form_date} onChange={handleChange} data-testid="vg-date" />
              </div>
              <div>
                <label htmlFor="term" className="block text-sm font-bold text-teal-800 uppercase tracking-wide mb-1">
                  Term (if applicable)
                </label>
                <Input id="term" name="term" value={formData.term} onChange={handleChange} data-testid="vg-term" />
              </div>
              <div>
                <label htmlFor="committees" className="block text-sm font-bold text-teal-800 uppercase tracking-wide mb-1">
                  Committee(s) / Workgroup(s)
                </label>
                <Input id="committees" name="committees" value={formData.committees} onChange={handleChange} data-testid="vg-committees" />
              </div>
            </div>

            <div>
              <SectionHeader icon={Landmark} num="1" title="My Goal & Vision for DCELC as an Organization"
                subtitle="What is one key goal you hope DCELC achieves in the next 1–3 years? Why is this important?"
                color={{ bg: 'bg-teal-50', badge: 'bg-teal-700', text: 'text-teal-800' }} />
              <label htmlFor="goal_organization" className="sr-only">My goal and vision for DCELC as an organization</label>
              <Textarea id="goal_organization" name="goal_organization" required rows={5}
                placeholder="My goal and vision for DCELC is…" value={formData.goal_organization} onChange={handleChange} data-testid="vg-goal-org" />
            </div>

            <div>
              <SectionHeader icon={User} num="2" title="My Goal & Vision for My Role on the Board"
                subtitle="What is one key contribution or impact you want to make in your role as a board member?"
                color={{ bg: 'bg-lime-50', badge: 'bg-lime-700', text: 'text-lime-800' }} />
              <label htmlFor="goal_role" className="sr-only">My goal and vision for my role on the DCELC board</label>
              <Textarea id="goal_role" name="goal_role" required rows={5}
                placeholder="My goal and vision for my role on the DCELC Board is…" value={formData.goal_role} onChange={handleChange} data-testid="vg-goal-role" />
            </div>

            <div>
              <SectionHeader icon={Handshake} num="3" title="How DCELC Can Best Support You"
                subtitle="What resources, information, or support would help you be most effective in achieving your vision?"
                color={{ bg: 'bg-amber-50', badge: 'bg-amber-600', text: 'text-amber-800' }} />
              <label htmlFor="support_needed" className="sr-only">How DCELC can best support you</label>
              <Textarea id="support_needed" name="support_needed" rows={4} value={formData.support_needed} onChange={handleChange} data-testid="vg-support" />
            </div>

            <div className="bg-slate-50 rounded-xl p-4 text-center text-sm text-slate-600 italic">
              &ldquo;We are committed to advancing high-quality, equitable early learning opportunities for every child
              and family in Washington, DC.&rdquo; — Thank you for your leadership, partnership, and dedication.
            </div>

            <Button type="submit" disabled={submitting} className="w-full bg-teal-700 hover:bg-teal-800 text-white py-6 text-base" data-testid="vision-goals-submit">
              {submitting ? 'Submitting…' : 'Submit My Vision & Goals'}
            </Button>
            <p className="text-center text-xs font-bold text-[#0a1628]">
              YOUR VOICE. <span className="text-teal-700">OUR MISSION.</span> <span className="text-amber-600">A STRONGER DC.</span>
            </p>
          </form>
        </div>
      </main>
      <Footer />
    </div>
  );
}
